import React, { useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { useDrag, useDrop } from "react-dnd";

const FAVOURITE_DRAG_TYPE = "PDF_EDITOR_FAVOURITE";

export const FavouriteDragItem = ({
  favourite,
  label,
  disabled = false,
  onReorder,
  children,
}) => {
  const suppressClickUntilRef = useRef(0);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: FAVOURITE_DRAG_TYPE,
      canDrag: !disabled,
      item: () => ({
        favouriteId: favourite.id,
        fieldId: favourite.fieldId,
        category: favourite.category,
        label: label || favourite.label,
      }),
      end: () => {
        // Prevent a drag release from also assigning by click.
        suppressClickUntilRef.current = Date.now() + 250;
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [favourite, label, disabled],
  );

  const [{ isReorderTarget }, drop] = useDrop(
    () => ({
      accept: FAVOURITE_DRAG_TYPE,
      canDrop: (item) =>
        !disabled &&
        item.category === favourite.category &&
        item.favouriteId !== favourite.id,
      drop: (item, monitor) => {
        if (monitor.didDrop()) return;

        onReorder?.(favourite, item.favouriteId);
        return { action: "reorder" };
      },
      collect: (monitor) => ({
        isReorderTarget: monitor.isOver({ shallow: true }) && monitor.canDrop(),
      }),
    }),
    [favourite, disabled, onReorder],
  );

  const connectRef = useCallback(
    (node) => {
      drag(drop(node));
    },
    [drag, drop],
  );

  return (
    <Box
      ref={connectRef}
      component="span"
      onClickCapture={(event) => {
        if (Date.now() < suppressClickUntilRef.current) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      sx={{
        display: "inline-flex",
        opacity: isDragging ? 0.4 : 1,
        outline: isReorderTarget ? "2px solid #1976d2" : "none",
        outlineOffset: 2,
        borderRadius: 1.5,
      }}
    >
      {children}
    </Box>
  );
};

export const FavouriteWidgetDropTarget = ({
  widgetName,
  rect,
  disabled = false,
  onAssign,
}) => {
  const [{ isFavouriteDrag, isOver, canDrop, label }, drop] = useDrop(
    () => ({
      accept: FAVOURITE_DRAG_TYPE,
      canDrop: (item) =>
        !disabled &&
        Boolean(widgetName) &&
        item.fieldId !== undefined &&
        item.fieldId !== null &&
        typeof onAssign === "function",
      drop: (item, monitor) => {
        if (monitor.didDrop()) return;

        onAssign(item.fieldId, widgetName);
        return { action: "assign", widgetName };
      },
      collect: (monitor) => ({
        isFavouriteDrag: monitor.getItemType() === FAVOURITE_DRAG_TYPE,
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
        label: monitor.getItem()?.label || "field",
      }),
    }),
    [widgetName, disabled, onAssign],
  );

  const connectRef = useCallback(
    (node) => {
      drop(node);
    },
    [drop],
  );

  const active = isFavouriteDrag && !disabled;
  const highlighted = active && isOver && canDrop;

  return (
    <Box
      ref={connectRef}
      sx={{
        position: "absolute",
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        boxSizing: "border-box",
        zIndex: 250,
        pointerEvents: active ? "auto" : "none",
        borderRadius: "3px",
        outline: highlighted ? "2px dashed #16a34a" : "none",
        outlineOffset: "2px",
        backgroundColor: highlighted
          ? "rgba(22, 163, 74, 0.10)"
          : "transparent",
        boxShadow: highlighted ? "0 0 0 5px rgba(22, 163, 74, 0.08)" : "none",
        transition: "background-color 120ms ease, box-shadow 120ms ease",
      }}
    >
      {highlighted && (
        <Box
          component="span"
          sx={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            maxWidth: "min(360px, 70vw)",
            px: 1.25,
            py: 0.5,
            borderRadius: "6px",
            bgcolor: "#166534",
            color: "#fff",
            boxShadow: "0 3px 10px rgba(0, 0, 0, 0.16)",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
            pointerEvents: "none",
          }}
        >
          <Box component="span" sx={{ flexShrink: 0 }}>
            Drop to assign
          </Box>

          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: 700,
            }}
          >
            {label}
          </Box>
        </Box>
      )}
    </Box>
  );
};
